import classNames from 'classnames'
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { useSource } from 'core-components/library/list/'
import { ISubMenuProps, SubMenu } from 'core-components/library/menu'
import {
  AbsolutePopup,
  IPopupContentData,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
  TVisibilityTrigger,
} from 'core-components/library/popup'
import { BaseCrud } from 'core-components/types'

import { ITableProps } from '../list/types'

import styles from './Menu.module.scss'

export interface IMenuProps<T = IForm, R = string> {
  /**
   * A Class name for component root node
   */
  className?: string
  /**
   * Classes for component parts
   */
  classes?: {
    root?: string
    popupWrap?: string
    popPaper?: string
  }
  /**
   * Callback function fires when value is selected
   */
  onChoose: (id: R) => any
  /**
   * Source for menu
   */
  source: BaseCrud<any>
  /**
   * Triggering JSX element
   */
  content: () => JSX.Element
  /**
   * Field from source to display
   */
  caption?: string
  /**
   * Filter for list data
   */
  dataPreprocessPredicate?: (item: any) => boolean
  /**
   * Classes for submenu parts
   */
  subMenuClasses?: ISubMenuProps['subMenuClasses']
  /**
   * Vertical direction of the popup menu
   */
  verticalDirection?: TPopupVertical
  /**
   * Horizontal direction of the popup menu
   */
  horizontalDirection?: TPopupHorizontal
  /**
   * Horizontal start position of the popup menu
   */
  horizontalStartPosition?: TPopupHorizontal
  /**
   * SubMenu Popup Positioning Options
   */
  subMenuPopupOptions?: ISubMenuProps['subMenuPopupOptions']
  /**
   * Change the action type to show menu
   */
  trigger?: TVisibilityTrigger
  /**
   * Custom list item template
   */
  itemTpl?: (item: IForm) => JSX.Element
  /**
   * Hierarchy key field
   */
  hierarchyField?: string
  /**
   * Change the action type to show submenu
   */
  subMenuTrigger?: TVisibilityTrigger
  /**
   *  Get all the downloaded data
   */
  onDataLoad?: ITableProps<T, R>['onDataLoad']
  /**
   * Do not use, for testing purposes only!
   */
  _popupVisible?: boolean // for tests
  /**
   * Popup test ID. This property is used for automated tests.
   */
  __popupTestId?: string
}

export const Menu = <T = IForm, R = string>({
  className,
  classes,
  onChoose,
  source,
  content,
  caption,
  dataPreprocessPredicate,
  subMenuClasses,
  subMenuTrigger = TVisibilityTrigger.Hover,
  horizontalDirection = TPopupHorizontal.Right,
  verticalDirection = TPopupVertical.Bottom,
  horizontalStartPosition = TPopupHorizontal.Left,
  subMenuPopupOptions,
  trigger = TVisibilityTrigger.Click,
  itemTpl,
  hierarchyField,
  onDataLoad,
  _popupVisible,
  __popupTestId,
}: IMenuProps<T, R>) => {
  const [sourceUpdateCount, setSourceUpdateCount] = useState(-1)
  const [hoveredRows, setHoveredRows] = useState<{ [key: string]: string }>({})
  const updateRowsRef = useRef<number>(0)

  const {
    data,
    openNode,
    wip: isDataLoading,
  } = useSource({ source, sourceUpdateCount, onDataLoad })

  let vStartPosition = verticalDirection
  // avoid connecting the popup window to the trigger element only by corner to corner
  if (
    (horizontalStartPosition === TPopupHorizontal.Left &&
      horizontalDirection === TPopupHorizontal.Left) ||
    (horizontalStartPosition === TPopupHorizontal.Right &&
      horizontalDirection === TPopupHorizontal.Right)
  ) {
    if (verticalDirection === TPopupVertical.Bottom) {
      vStartPosition = TPopupVertical.Top
    }
    if (verticalDirection === TPopupVertical.Top) {
      vStartPosition = TPopupVertical.Bottom
    }
  }

  const visibilityCallback = useCallback(
    (newVisible: boolean) => {
      return _popupVisible || newVisible
    },
    [_popupVisible]
  )

  const updateHoveredRows = useCallback((newValue: { [key: string]: string }) => {
    if (updateRowsRef.current) {
      clearTimeout(updateRowsRef.current)
    }

    updateRowsRef.current = window.setTimeout(() => {
      setHoveredRows((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(newValue)) {
          return newValue
        }

        return prev
      })
    }, 50)
  }, [])

  const popupContent = useCallback(
    (popupContent: IPopupContentData) => {
      return (
        <PopPaper className={classNames(styles.popup, classes?.popPaper || '')}>
          <SubMenu<T, R>
            isDataLoading={isDataLoading}
            subMenuClasses={subMenuClasses}
            subMenuPopupOptions={subMenuPopupOptions}
            onChoose={async (id) => {
              const isDisabled = await onChoose(id)
              if (!isDisabled) {
                popupContent.action.close()
              }
            }}
            data={data}
            itemTpl={itemTpl}
            source={source}
            root={source.hierarchyRoot as string}
            caption={caption}
            dataPreprocessPredicate={dataPreprocessPredicate}
            openNode={openNode}
            hierarchyField={hierarchyField}
            subMenuTrigger={subMenuTrigger}
            hoveredRows={hoveredRows}
            setHoveredRows={updateHoveredRows}
          />
        </PopPaper>
      )
    },
    [
      classes?.popPaper,
      isDataLoading,
      subMenuClasses,
      subMenuPopupOptions,
      data,
      itemTpl,
      source,
      caption,
      dataPreprocessPredicate,
      openNode,
      hierarchyField,
      subMenuTrigger,
      hoveredRows,
      updateHoveredRows,
      onChoose,
    ]
  )

  const target = useMemo(() => content(), [content])

  useEffect(() => {
    setSourceUpdateCount((prev) => prev + 1)
  }, [source])

  return (
    <AbsolutePopup
      rootClassName={classNames(className || '', classes?.root || '')}
      popupClassName={classNames(styles.popupWrap, classes?.popupWrap || '')}
      visibilityTrigger={trigger}
      visibilityCallback={visibilityCallback}
      verticalDirection={verticalDirection}
      horizontalDirection={horizontalDirection}
      horizontalStartPosition={horizontalStartPosition}
      verticalStartPosition={vStartPosition}
      popupContent={popupContent}
      __popupTestId={__popupTestId}
    >
      {target}
    </AbsolutePopup>
  )
}

export default Menu
